import { useEffectWithCleanup, useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import i18next from "i18next";
import { html } from "haunted";
import { VOUCHER_REGEX, PROMO_CODE_REGEX } from "Services/constants";
import { JetSmartEvent } from "Services/eventbus/JetSmartEvent";
import { tealiumLog } from "Services/TealiumHelpers";
import { getPromoCode } from "ComponentHelpers/PromoCodeHelper";
import { tealiumLogPreloadedPromoCode } from "ComponentHelpers/SearchboxTealiumHelpers";
import { useReduxState } from "Shared/redux/useReduxState";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
}

export const usePromoCodeBox = (props: Props) => {
	const [userInfo] = useReduxState("userInfo");
	const [currentSearch, setCurrentSearch] = useReduxState("currentSearch");

	const [predefinedPromoCode, setPredefinedPromoCode] = useState<string>("");
	const [showPromoCodeFormatError, setShowPromoCodeFormatError] = useState<boolean>(false);
	const [showPromoCodeVoucherError, setShowPromoCodeVoucherError] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);

	// Helpers

	const init = () => {
		if (
			isInitialized ||
			!userInfo?.RoleCode ||
			!props.dynamicSettings ||
			currentSearch.promoCode ||
			userInfo.ChileCompra?.IsMember
		) {
			return;
		}

		try {
			const promoCode = getPromoCode(userInfo, props.dynamicSettings, props.culture);

			setCurrentSearch({ ...currentSearch, promoCode });
			setPredefinedPromoCode(promoCode);
			tealiumLogPreloadedPromoCode(promoCode);
			setIsOpen(!!promoCode);
			setIsInitialized(true);
		} catch (e) {
			throw new Error("Unable to parse user info cookie or set WA pc.");
		}
	};

	const validate = () => {
		setShowPromoCodeVoucherError(false);
		setShowPromoCodeFormatError(false);

		if (!currentSearch.promoCode) return true;

		if (VOUCHER_REGEX.test(currentSearch.promoCode)) {
			setShowPromoCodeVoucherError(true);

			tealiumLog({
				eventName: "validation_error",
				eventParams: {
					page: "/",
					message: "[promo code appears to be a voucher]",
				},
			});

			return false;
		}

		if (!PROMO_CODE_REGEX.test(currentSearch.promoCode)) {
			setShowPromoCodeFormatError(true);

			tealiumLog({
				eventName: "validation_error",
				eventParams: {
					page: "/",
					message: "[invalid promo code format]",
				},
			});

			return false;
		}

		return true;
	};

	const displayedPromoCode = () =>
		predefinedPromoCode === currentSearch.promoCode ? getMaskedPromoCode() : currentSearch.promoCode;

	const getMaskedPromoCode = () => {
		if (!currentSearch.promoCode) return "";

		const lengthToMask =
			userInfo?.PeruCompra?.IsAdmin || userInfo?.PeruCompra?.IsMember
				? currentSearch.promoCode.length
				: currentSearch.promoCode.length - 2;

		return `${[...Array(lengthToMask)].map(() => "*").join("")}${currentSearch.promoCode.substring(lengthToMask)}`;
	};

	// Event listeners

	const handleBlur = () => {
		window.eventBus.raiseEvent({
			name: JetSmartEvent.PromoCodeAdded,
			params: {
				id: "searchbox.promocode",
				event: "input",
				data: {
					code: currentSearch.promoCode,
				},
			},
		});
	};

	const handleInput = (promoCode: string) => {
		setShowPromoCodeVoucherError(false);
		setShowPromoCodeFormatError(false);

		setCurrentSearch({ ...currentSearch, promoCode });
	};

	useEffectWithoutCleanup(() => {
		if (currentSearch.useAmericanMiles && userInfo?.AmericanAirlines?.IsMember) {
			setShowPromoCodeVoucherError(false);
			setShowPromoCodeFormatError(false);
			setCurrentSearch({ ...currentSearch, promoCode: "" });
		}
	}, [currentSearch.useAmericanMiles, userInfo?.AmericanAirlines?.IsMember]);

	useEffectWithCleanup(init, [
		userInfo?.RoleCode,
		props.culture,
		props.dynamicSettings,
		currentSearch,
		isInitialized,
	]);

	// Templates

	const errorTemplate = () =>
		showPromoCodeFormatError || showPromoCodeVoucherError
			? html`
					<div class="absolute inset-x-0 top-full bg-white p-1 text-xs text-n-red md:bottom-full md:top-auto">
						${!showPromoCodeVoucherError
							? i18next.t("promoCodeVoucherError")
							: i18next.t("promoCodeFormatError")}
					</div>
			  `
			: "";

	const showPromoCode = () =>
		!userInfo?.ChileCompra?.IsMember && (!currentSearch.useAmericanMiles || !userInfo?.AmericanAirlines?.IsMember);

	const promoCodeTemplate = () =>
		isOpen
			? html`
					<div class="relative">
						<ac-input
							.autoComplete=${"off"}
							.isInvalid=${showPromoCodeFormatError || showPromoCodeVoucherError}
							.isDisabled=${userInfo?.PeruCompra?.IsAdmin || userInfo?.PeruCompra?.IsMember}
							.isSmall=${true}
							.placeholder=${i18next.t("search-box-promo-code")}
							.value=${displayedPromoCode()}
							.onInput=${handleInput}
							.onBlur=${handleBlur}
						></ac-input>

						${errorTemplate()}
					</div>
			  `
			: html`
					<div
						class="flex cursor-pointer items-center gap-1.5 text-n-blue hover:text-n-cyan"
						@click=${() => setIsOpen(true)}
					>
						<span class="pb-0.5 text-2xl">+</span>
						<span class="text-nowrap text-sm tw-hidden md:block"
							>${i18next.t("Añadir código promocional")}</span
						>
						<span class="text-nowrap text-sm md:hidden">${i18next.t("Código promocional")}</span>
					</div>
			  `;

	const htmlTemplate = () => (showPromoCode() ? promoCodeTemplate() : html``);

	return { predefinedPromoCode, htmlTemplate, validate };
};
